/**
 * GroupHeading Component (ORCH-410 §4.2)
 *
 * Purpose: The one heading style for a grouped picker: the non-selectable band
 *          over each group of an Autocomplete's options. RoleSelect (Standard
 *          Roles / Custom Roles), ListFieldRenderer's `groupBy` lists (Common
 *          locales, Company's Agencies / Advertisers) all render it, so every
 *          grouped list in the app reads the same.
 * Logic: A MUI ListSubheader that sticks while its group scrolls, with pointer
 *        events off so it is never hovered or picked. `top` is where it sticks:
 *        below a sticky "Add New" row when the list has one.
 * Dependencies: MUI ListSubheader
 */

import React from 'react';
import { Box, ListSubheader } from '@mui/material';

export interface GroupHeadingProps {
  /** The group's name, as the picker's groupBy returns it */
  group: string;
  /** Offset the heading sticks at, in px (default 0) */
  top?: number;
  dataTestId?: string;
  children?: React.ReactNode;
}

/** A grouped picker's heading band and the options under it. */
const GroupHeading: React.FC<GroupHeadingProps> = ({ group, top = 0, dataTestId, children }) => (
  <li>
    <ListSubheader
      component="div"
      data-testid={dataTestId}
      sx={{
        // An opaque band while it sticks; a heading, so never hovered or picked
        top,
        bgcolor: 'grey.200',
        color: 'text.secondary',
        fontWeight: 600,
        lineHeight: '32px',
        pointerEvents: 'none',
      }}
    >
      {group}
    </ListSubheader>
    <Box component="ul" sx={{ p: 0 }}>
      {children}
    </Box>
  </li>
);

export default GroupHeading;
